import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Workflow, Copy, Vote, MessagesSquare } from 'lucide-react';
import { verisApi } from '@/services/api.js';
import { Card, CardHeader, StatCard, QuickAction, EmptyState, ErrorPanel } from '@/components/common/Card.jsx';
import StatusBadge from '@/components/common/StatusBadge.jsx';
import DeferenceBanner from '@/components/veris/DeferenceBanner.jsx';
import { fmtRelative, fmtPercent } from '@/utils/format.js';

export default function VerisHome() {
  const { t } = useTranslation();
  const queueQuery = useQuery({
    queryKey: ['veris', 'registrations', 'pending'],
    queryFn:  () => verisApi.listRegistrations({ status: 'pending', per_page: 6 }),
    retry: 1,
  });

  const matchesQuery = useQuery({
    queryKey: ['veris', 'dedup', 'pending'],
    queryFn:  () => verisApi.listDedupMatches({ status: 'pending', per_page: 6 }),
    refetchInterval: 30000,
    retry: 1,
  });

  const queue   = queueQuery.data?.data   ?? [];
  const matches = matchesQuery.data?.data ?? [];

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{t('home.verisHome.verisDashboard')}</h1>
        <p className="mt-1.5 text-sm text-slate-600">{t('home.verisHome.registrationIntakeDedupMatchResolutionEligibility')}</p>
      </div>

      <DeferenceBanner />


      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <StatCard label={t('home.verisHome.pendingRegistrations')} value={queueQuery.isLoading ? '…' : queue.length}     hint={t('home.verisHome.awaitingVerification')}  accent="amber" />
        <StatCard label={t('home.verisHome.dedupMatches')}         value={matchesQuery.isLoading ? '…' : matches.length} hint={t('home.verisHome.awaitingResolution')}    accent="rose" />
        <StatCard label={t('home.verisHome.channelsLive')}         value="OK"                                            hint={t('home.verisHome.realTimeWired')}        accent="sky" />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-10">
        <QuickAction to="/veris/queue"       title={t('home.verisHome.registrations')}  description={t('home.verisHome.reviewPendingAndVerifiedRegistrations')}        icon={Workflow} />
        <QuickAction to="/veris/dedup"       title={t('home.verisHome.dedupMatches')}  description={t('home.verisHome.resolveDeterministicProbabilisticMatches')}   icon={Copy} />
        <QuickAction to="/veris/eligibility" title={t('home.verisHome.eligibility')}    description={t('home.verisHome.manageEligibilityStateMachinePerRegistrant')} icon={Vote} />
        <QuickAction to="/veris/disputes"    title={t('home.verisHome.disputes')}       description={t('home.verisHome.guidedSignedDisputeWorkflow')}                 icon={MessagesSquare} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('home.verisHome.pendingRegistrations')} description={t('home.verisHome.newestPendingIntakeRecords')} />
          <div className="mt-5">
            {queueQuery.isLoading ? (
              <SkeletonList rows={5} />
            ) : queueQuery.isError ? (
              <ErrorPanel error={queueQuery.error} onRetry={queueQuery.refetch} />
            ) : queue.length === 0 ? (
              <EmptyState title={t('home.verisHome.noPendingRegistrations')} description={t('home.verisHome.newIntakeRecordsWillAppearHere')} />
            ) : (
              <ul className="divide-y divide-slate-100">
                {queue.map(r => (
                  <li key={r.id} className="flex items-center gap-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {[r.first_name, r.last_name].filter(Boolean).join(' ') || 'Registrant'} · <span className="text-slate-500">{r.jurisdiction_code}</span>
                      </div>
                      <div className="text-xs text-slate-500">{fmtRelative(r.received_at || r.created_at)}</div>
                    </div>
                    <StatusBadge status={r.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title={t('home.verisHome.dedupMatches')} description={t('home.verisHome.awaitingReviewerResolutionConfirmOrOverride')} />
          <div className="mt-5">
            {matchesQuery.isLoading ? (
              <SkeletonList rows={5} />
            ) : matchesQuery.isError ? (
              <ErrorPanel error={matchesQuery.error} onRetry={matchesQuery.refetch} />
            ) : matches.length === 0 ? (
              <EmptyState title={t('home.verisHome.noMatchesNeedingReview')} description={t('home.verisHome.detectedMatchesWillQueueUpHere')} />
            ) : (
              <ul className="divide-y divide-slate-100">
                {matches.map(m => (
                  <li key={m.id} className="flex items-center gap-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {m.classification === 'deterministic' ? 'Deterministic match' : 'Probabilistic match'}
                        <span className="ml-1 text-slate-500">{t('home.verisHome.score')} {fmtPercent(m.match_score)}</span>
                      </div>
                      <div className="text-xs text-slate-500">{fmtRelative(m.created_at)}</div>
                    </div>
                    <StatusBadge status={m.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}

function SkeletonList({ rows = 3 }) {
  return (
    <ul className="divide-y divide-slate-100">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="flex items-center gap-4 py-3">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-1/2 rounded-full bg-slate-200 animate-pulse" />
            <div className="h-2.5 w-1/3 rounded-full bg-slate-100 animate-pulse" />
          </div>
          <div className="h-5 w-16 rounded-full bg-slate-100 animate-pulse" />
        </li>
      ))}
    </ul>
  );
}
